import React, { useEffect, useRef, useState, Fragment } from 'react';
import 'aframe';
import '../../primitives/hotspot';

import { usePrevious } from '../../../../hooks';
import { removeAnchorElement } from 'utils/stringHelpers';

import AframeHtmlElement from '../../components/AframeHtmlElement';
import AframeIcon from '../../components/AframeIcon';
import AframeTitle from '../../components/AframeTitle';
import HotspotModal from 'components/Hotspot/components/HotspotModal/HotspotModal';

const defaultContentWidth = 8;

const TextHotspot = ({
  active,
  showBaseHotspot,
  cameraPosition,
  darkMode,
  hotspotId,
  html,
  isBeingEdited,
  isDisabled,
  onClick,
  position,
  showLabel,
  rotation,
  contentSize,
  title,
  tourId,
  tourName,
  showHotspotModal,
  aid,
  hidden = false,
  outWorldDisplay = false,
  iconSize,
  hideGlow,
  isInEditor = false,
  icon,
  iconSrc,
  ariaLabel,
}) => {
  const rigId = `rig_${hotspotId}`;
  const titleId = showLabel ? `title_${hotspotId}` : null;
  const contentId = `content_${hotspotId}`;
  const iconId = `icon_${hotspotId}`;
  const htmlElementId = `text-hotspot__${hotspotId}`;

  const hotspotRef = useRef();

  const iconButtonRef = useRef();
  const textPlane = useRef();
  const prevHTML = usePrevious(html);
  const prevDarkMode = usePrevious(darkMode);
  const prevTextBoxSize = usePrevious(contentSize);
  const [centerHotspot, setCenterHotspot] = useState(false);

  useEffect(() => {
    const text = textPlane.current;

    if (
      (prevHTML && html !== prevHTML && text) ||
      (typeof prevDarkMode === 'boolean' && prevDarkMode !== darkMode) ||
      (prevTextBoxSize && prevTextBoxSize !== contentSize)
    ) {
      text?.components?.material?.shader?.__render();
    }
  }, [
    darkMode,
    prevDarkMode,
    prevHTML,
    html,
    prevTextBoxSize,
    contentSize,
    onClick,
    hotspotId,
    isDisabled,
    showHotspotModal,
  ]);

  useEffect(() => {
    const hotspot = hotspotRef?.current;
    if (outWorldDisplay && hotspot && !isInEditor) {
      hotspot.setAttribute('visible', !hidden);
    }
  }, [outWorldDisplay, hidden, isInEditor]);

  const width =
    typeof contentSize?.size === 'number'
      ? contentSize?.size
      : defaultContentWidth;
  const height = width / 1.6;
  const contentPosition = `0 ${height / 2 + 1} -0.4`;
  const customIconId = `custom-icon_${hotspotId}`;
  const customAssetId = `custom-asset_${hotspotId}`;
  const focusId = `focus-outline_${hotspotId}`;
  const useCustomIcon = icon && iconSrc;
  const hasHyperlink = html?.indexOf('href');
  const modifiedHtml =
    !outWorldDisplay && hasHyperlink && removeAnchorElement(html);

  return (
    <Fragment>
      <a-entity id={rigId} rotation={rotation}>
        <a-hotspot
          ref={hotspotRef}
          active={active && showBaseHotspot}
          content={contentId}
          darkmode={darkMode}
          disabled={isDisabled}
          hotspotName={title}
          id={`hotspot_${hotspotId}`}
          hotspotid={hotspotId}
          focusoutlineicon={focusId}
          icon={useCustomIcon ? customIconId : iconId}
          position={position}
          rotateto={cameraPosition}
          hotspottitle={titleId}
          translatecontent={`0 ${height / 2 + 1} 0`}
          tourId={tourId}
          tourName={tourName}
          type={'text'}
          underedits={isBeingEdited}
          aid={aid}
          centerHotspot={centerHotspot}
          data-testid={`hotspot_${hotspotId}`}
          data-cy={`hotspot_${hotspotId}`}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
        >
          {!outWorldDisplay && (
            <AframeHtmlElement
              backgroundColor={darkMode ? '#000000E5' : '#FFFFFFF2'}
              color={darkMode ? 'white' : 'black'}
              forwardRef={textPlane}
              htmlId={htmlElementId}
              planeId={contentId}
              text={modifiedHtml || html}
              position={contentPosition}
              height={height}
              width={width}
              htmlHeight={height * 200}
              htmlWidth={width * 200}
              visible="false"
            />
          )}
          {showLabel && (
            <AframeTitle
              iconSize={iconSize}
              id={titleId}
              value={title}
              darkMode={darkMode}
            />
          )}
          <AframeIcon
            forwardRef={iconButtonRef}
            src={
              icon
                ? `#${customAssetId}`
                : `#hotspot__text-icon${darkMode ? '-black' : ''}`
            }
            id={icon ? customIconId : iconId}
            data-testid={icon ? customIconId : iconId}
            iconSize={iconSize}
            hideGlow={hideGlow}
            focusId={focusId}
          />
        </a-hotspot>
        {/* a custom icon that only applies to a single hotspot */}
        {useCustomIcon && (
          <a-asset>
            <img
              src={iconSrc}
              id={customAssetId}
              crossOrigin="anonymous"
              alt=""
            />
          </a-asset>
        )}
      </a-entity>
      {outWorldDisplay && (
        <HotspotModal
          content={html}
          name={title}
          closeModal={() => {
            onClick(hotspotId, isDisabled);
            setCenterHotspot(false);
          }}
          showModal={active && !showBaseHotspot}
          hotspotId={hotspotId}
          type={'text'}
          isBeingEdited={isBeingEdited}
        />
      )}
    </Fragment>
  );
};

export default TextHotspot;
